import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Panel } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { Field } from '@/shared/ui/Modal'
import { explain } from '@/shared/api/problem'
import { cn } from '@/lib/utils'
import type {
  SurveyDesignReadinessDto, SurveyResponseDto, SurveySampleSizeDto,
} from '@/shared/api/types'
import { getDesignReadiness, getSampleSize, updatePoll } from './api'
import { count, num } from './vocab'

/**
 * Step 2 — how many interviews, and whether that buys the precision claimed.
 *
 * ## The calculator is the point of this step
 *
 * `targetMoe` and `confidenceLevel` have been columns since the first polling
 * migration and were read by nothing. A poll could declare ±3% at 99% with a
 * target of 200 and nothing anywhere said the second cannot deliver the first.
 *
 * ## Every number on this panel comes from the server
 *
 * There is no arithmetic in this file. `requiredN`, the adequacy percentage and
 * the achieved margin are all computed by `GET /surveys/{id}/sample-size`, with
 * the finite-population correction applied against the poll's declared frame —
 * which is why a ward poll asks for 843 interviews and not 1,068. Recomputing
 * any of it here would be a second implementation to disagree with, and this
 * feature has a standing rule against exactly that.
 *
 * ## Strata and quotas are NOT duplicated here
 *
 * They already have a screen with three editors on it. This step states what is
 * declared and links to it, rather than growing a second editor for the same
 * rows — two editors for one table is how they drift.
 */
export function ConfigSampleStep({
  poll, onSaved,
}: {
  poll: SurveyResponseDto
  onSaved: (poll: SurveyResponseDto) => void
}) {
  const [target, setTarget] = useState(String(poll.targetSample ?? 400))
  const [moe, setMoe] = useState(poll.targetMoe == null ? '5' : String(poll.targetMoe))
  const [confidence, setConfidence] = useState(String(poll.confidenceLevel ?? 95))

  const [verdict, setVerdict] = useState<SurveySampleSizeDto | null>(null)
  const [verdictError, setVerdictError] = useState<string | null>(null)
  const [readiness, setReadiness] = useState<SurveyDesignReadinessDto | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setTarget(String(poll.targetSample ?? 400))
    setMoe(poll.targetMoe == null ? '5' : String(poll.targetMoe))
    setConfidence(String(poll.confidenceLevel ?? 95))
  }, [poll.id])

  useEffect(() => {
    let alive = true
    getDesignReadiness(poll.id)
      .then((r) => { if (alive) setReadiness(r) })
      .catch(() => { if (alive) setReadiness(null) })
    return () => { alive = false }
  }, [poll.id, poll.version])

  /**
   * The verdict is re-asked as the fields change, through the OVERRIDES.
   *
   * That is what the three query parameters on the endpoint are for: without
   * them somebody would have to write a target, save it, read the verdict and
   * write it again. Debounced, because it fires on every keystroke otherwise.
   */
  useEffect(() => {
    const marginOfError = Number(moe)
    if (!marginOfError || marginOfError <= 0) {
      setVerdict(null)
      setVerdictError('Set a target margin of error to size the sample.')
      return
    }
    let alive = true
    const timer = setTimeout(() => {
      getSampleSize(poll.id, {
        marginOfError,
        confidenceLevel: Number(confidence) || 95,
        targetSample: Number(target) || 0,
      })
        .then((v) => { if (alive) { setVerdict(v); setVerdictError(null) } })
        .catch((e) => { if (alive) { setVerdict(null); setVerdictError(explain(e)) } })
    }, 250)
    return () => { alive = false; clearTimeout(timer) }
  }, [poll.id, target, moe, confidence])

  async function save() {
    setBusy(true)
    setError(null)
    try {
      onSaved(await updatePoll(poll.id, {
        title: poll.title,
        description: poll.description,
        categoryCode: poll.categoryCode,
        purposeCode: poll.purposeCode,
        audienceCode: poll.audienceCode,
        frequencyCode: poll.frequencyCode,
        methodologyCode: poll.methodologyCode,
        scopeUnitId: poll.scopeUnitId,
        groupId: poll.groupId,
        waveId: poll.waveId,
        targetSample: Number(target) || 0,
        confidenceLevel: Number(confidence) || 95,
        targetMoe: moe ? Number(moe) : null,
        linkedSeatType: poll.linkedSeatType,
        audioRecordingMode: poll.audioRecordingMode,
        consentRequired: poll.consentRequired,
        randomiseQuestionOrder: poll.randomiseQuestionOrder,
        randomiseOptionOrder: poll.randomiseOptionOrder,
        allowOffline: poll.allowOffline,
        opensAt: poll.opensAt,
        closesAt: poll.closesAt,
        retentionDays: poll.retentionDays,
      }))
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  const adequacy = verdict?.adequacyPct ?? 0
  const tone = adequacy >= 100 ? 'good' : adequacy >= 70 ? 'warn' : 'bad'

  return (
    <div className="space-y-4">
      <Panel
        title="Sample size and precision"
        description="How many interviews, and what that buys"
        right={(
          <button type="button" className="erp-btn-primary h-8 text-erp-xs"
            disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        )}
      >
        <div className="space-y-4">
          <ErrorStrip message={error} />

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Target sample" hint="Interviews to collect.">
              <input className="erp-input h-9 w-full font-mono" type="number" min={0}
                value={target} onChange={(e) => setTarget(e.target.value)} />
            </Field>
            <Field label="Margin of error (pp)" hint="±this many percentage points.">
              <input className="erp-input h-9 w-full font-mono" type="number" step="0.5" min={0}
                value={moe} onChange={(e) => setMoe(e.target.value)} />
            </Field>
            <Field label="Confidence level" hint="Higher is more conservative.">
              <select className="erp-input h-9 w-full font-mono" value={confidence}
                onChange={(e) => setConfidence(e.target.value)}>
                <option value="90">90%</option>
                <option value="95">95%</option>
                <option value="99">99%</option>
              </select>
            </Field>
          </div>

          {verdictError && <Note tone="bad">{verdictError}</Note>}

          {verdict && (
            <div className={cn(
              'border rounded-md p-3',
              tone === 'good' && 'bg-emerald-50/60 border-emerald-200',
              tone === 'warn' && 'bg-amber-50/60 border-amber-200',
              tone === 'bad' && 'bg-red-50/60 border-red-200',
            )}>
              <div className="grid gap-3 sm:grid-cols-4">
                <Figure label="Required" value={count(verdict.requiredN)}
                  hint={`for ±${num(verdict.marginOfErrorPct, 1)}pp at ${verdict.confidenceLevel}%`} />
                <Figure label="Your target" value={count(verdict.targetSample)} />
                <Figure label="Adequacy" value={`${verdict.adequacyPct}%`}
                  tone={tone} />
                <Figure label="Your target buys"
                  value={verdict.achievedMoePct == null
                    ? '—' : `±${num(verdict.achievedMoePct, 2)}pp`}
                  hint="at this confidence level" />
              </div>
              <p className="text-erp-xs text-muted-foreground mt-2">{verdict.sentence}</p>
              <p className="text-erp-xs text-muted-foreground mt-1">
                <span className="font-mono">n₀ = z²·p(1−p)/E²</span> with p = 0.5, the worst
                case over every possible result — right for PLANNING and wrong for reporting,
                which is why the published margin on the Results screen is computed on the
                estimated proportion instead.
                {verdict.finitePopulationCorrected
                  ? ` Corrected against the declared population of ${count(verdict.populationN ?? 0)}.`
                  : ' No population frame is declared, so this is the uncorrected requirement —'
                    + ' declaring one can only lower it.'}
              </p>
            </div>
          )}
        </div>
      </Panel>

      <Panel title="Sampling design"
        description="Strata, quota cells and the population frame"
        right={(
          <Link to={`/polling/sample?poll=${poll.id}`}
            className="erp-btn-ghost h-8 text-erp-xs inline-flex items-center">
            Open the Sample screen
          </Link>
        )}
      >
        {!readiness ? <LoadingBlock label="Reading the design…" /> : (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <Figure label="Strata" value={count(readiness.strataCount)}
                hint={readiness.strataDeclared ? 'declared' : 'none declared'} />
              <Figure label="Quota cells" value={count(readiness.quotaCellCount)}
                hint={readiness.quotasDeclared ? 'declared' : 'none declared'} />
              <Figure label="Frame dimensions"
                value={readiness.frameDimensions.length
                  ? readiness.frameDimensions.join(', ') : '—'}
                hint={readiness.populationFrameDeclared
                  ? 'the poll can be raked' : 'without one the poll cannot be raked'} />
            </div>
            {readiness.blockers.length > 0 ? (
              <div className="space-y-1.5">
                {readiness.blockers.map((blocker) => (
                  <Note key={blocker} tone="bad">{blocker}</Note>
                ))}
              </div>
            ) : (
              <Note tone="good">
                The sampling design is complete. A weight run on this poll will produce
                projectable estimates rather than a NOT_WEIGHTED suppression.
              </Note>
            )}
            <p className="text-erp-xs text-muted-foreground">
              These rows are edited on the Sample screen rather than here. Two editors for one
              table is how the two drift, and that screen already carries the stratum, quota and
              frame dialogs.
            </p>
          </div>
        )}
      </Panel>
    </div>
  )
}

function Figure({ label, value, hint, tone }: {
  label: string; value: string; hint?: string; tone?: 'good' | 'warn' | 'bad'
}) {
  return (
    <div className="min-w-0">
      <p className="text-erp-xs text-muted-foreground">{label}</p>
      <p className={cn(
        'text-erp-lg font-semibold font-mono truncate',
        tone === 'good' && 'text-emerald-700',
        tone === 'warn' && 'text-amber-700',
        tone === 'bad' && 'text-red-700',
      )}>{value}</p>
      {hint && <p className="text-erp-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}
